import {
  addDays,
  familyHints,
  leisureHint,
  type Hint,
  type HintEvent,
  type LeisureDay,
} from '@shh/shared';
import { eventsOn, participants, type E } from './derive';
import type { FamilyData } from './family';
import type { Forecast } from './weather';
import { locale } from '../lib/locale';

const toHintEvent = (f: FamilyData, e: E): HintEvent => ({
  title: e.title,
  start: e.start,
  end: e.end,
  bring: e.bring ?? [],
  who: e.assignees === 'all' ? [] : participants(f, e).map((m) => m.name),
  routine: e.virtual === 'timetable',
  recurring: Boolean(e.repeat),
  waste: e.source === 'waste',
});

/**
 * The next days for the Freizeit hint: free = nothing planned except school and the bins, with
 * the weather where the forecast reaches.
 */
function leisureDays(f: FamilyData, weather?: Forecast): LeisureDay[] {
  return Array.from({ length: 4 }, (_, i) => {
    const date = addDays(f.today, i);
    const day = weather?.days.find((d) => d.date === date);
    return {
      date,
      free: !eventsOn(f, date).some((e) => e.source !== 'waste' && e.virtual !== 'timetable'),
      rain: day?.rain,
      max: day?.max,
    };
  });
}

/** The one Freizeit hint of the day (voucher running out, free weekend), or null. */
export const leisureHintFor = (f: FamilyData, weather?: Forecast) =>
  leisureHint({
    today: f.today,
    vouchers: f.vouchers,
    ideas: f.ideas,
    days: leisureDays(f, weather),
    locale,
  });

/**
 * "Denkt mit": the few hints worth saying out loud right now (see familyHints), plus at most
 * one from Freizeit (a voucher running out, a free weekend) – last, so today's things come first.
 */
export function hintsFor(f: FamilyData, now: string, weather?: Forecast, max = 3): Hint[] {
  const leisure = leisureHintFor(f, weather);
  const family = familyHints(
    {
      now,
      today: eventsOn(f, f.today).map((e) => toHintEvent(f, e)),
      tomorrow: eventsOn(f, addDays(f.today, 1)).map((e) => toHintEvent(f, e)),
      weather: weather && { rainFrom: weather.rainFrom },
    },
    leisure ? max - 1 : max,
    locale,
  );
  return leisure ? [...family, leisure] : family;
}
